import { Service } from '@genial/services';
import { Utils } from '@genial/utils';
import { selectorFamily } from 'recoil';

import { GenericMarketStatus, ProductWithMarketStatus } from './types';

export const productMarketStatusSelector = selectorFamily<
  GenericMarketStatus,
  { product: ProductWithMarketStatus }
>({
  key: 'productMarketStatusSelector',
  get:
    ({ product }) =>
    async () => {
      if (product === ProductWithMarketStatus.COES) {
        const { data: coesData } = await Service.COES.V1.getMarketStatus({ screenName: 'any' });

        return {
          isOpen: coesData.marketStatus.isOpened,
          message: coesData.marketStatus.message ?? '',
          hasOpeningHours: false,
          hasClosingHours: false,
        };
      } else if (product === ProductWithMarketStatus.TreasuryDirect) {
        const { data: treasuryDirectData } = await Service.Treasury.V1.getMarketStatus({
          screenName: 'any',
        });

        const openingDate = new Date(
          new Date().toISOString().split('T')[0] +
            `T${treasuryDirectData.marketStatus.startHour}:00-0300`
        );

        const closingDateDate = new Date(
          new Date().toISOString().split('T')[0] +
            `T${treasuryDirectData.marketStatus.finishHour}:00-0300`
        );

        return {
          isOpen: treasuryDirectData.marketStatus.isOpened,
          message: treasuryDirectData.marketStatus.message ?? '',
          hasOpeningHours: true,
          openingHour: openingDate,
          openingHourNormalized: Utils.Mask.DateFormat(openingDate, 'dd/MM/yyyy HH:mm'),
          hasClosingHours: true,
          closingHour: closingDateDate,
          closingHourNormalized: Utils.Mask.DateFormat(closingDateDate, 'dd/MM/yyyy HH:mm'),
        };
      } else if (product === ProductWithMarketStatus.Equity) {
        const { data: equityData } = await Service.Equities.V1.getMarketStatus({
          screenName: 'any',
        });

        return {
          isOpen: equityData.market.isOpened,
          message: equityData.market.status ?? '',
          hasOpeningHours: true,
          openingHour: equityData.market.openingTime,
          openingHourNormalized: Utils.Mask.DateFormat(
            equityData.market.openingTime,
            'dd/MM/yyyy HH:mm'
          ),
          hasClosingHours: true,
          closingHour: equityData.market.closingTime,
          closingHourNormalized: Utils.Mask.DateFormat(
            equityData.market.closingTime,
            'dd/MM/yyyy HH:mm'
          ),
        };
      } else if (product === ProductWithMarketStatus.FixedIncome) {
        const { data: fixedIncomeData } = await Service.FixedIncome.V1.getMarketStatus({
          screenName: 'any',
        });

        const validMarkedStatus = fixedIncomeData?.markets?.[1];

        if (!validMarkedStatus) {
          throw new Error('Fixed Income market not found');
        }

        return {
          isOpen: validMarkedStatus.isOpened,
          message: validMarkedStatus.status ?? '',
          hasOpeningHours: true,
          openingHour: validMarkedStatus.openingTime,
          openingHourNormalized: Utils.Mask.DateFormat(
            validMarkedStatus.openingTime,
            'dd/MM/yyyy HH:mm'
          ),
          hasClosingHours: true,
          closingHour: validMarkedStatus.closingTime,
          closingHourNormalized: Utils.Mask.DateFormat(
            validMarkedStatus.closingTime,
            'dd/MM/yyyy HH:mm'
          ),
        };
      } else {
        throw new Error('Product not found');
      }
    },
});
